import React, { useState, useEffect } from "react";

// Custom components
import toastManager from "../../../helpers/toastManager";
import AuthenticationCard from "../../../components/Authentication/AuthenticationCard";
import EmailSentPrompt from "../../../components/Authentication/EmailSentPrompt";
import PolicyCheckbox from "../../../components/Authentication/PolicyCheckbox";
import { RegularField } from "../../../components/InputFields/InputFields";

// API imports
import useAxios from "../../../hooks/useAxios";
import jsonFormat from "../../../apis/jsonFormat";

// Detect enter press
import { useEnterKeyPress } from "../../../hooks/useEnterKeyPress";

const EmailRegistrationScreen = () => {
  const [email, setEmail] = useState("");
  const [emailWarning, setEmailWarning] = useState(false);

  const [disclaimerIsChecked, setDisclaimerIsChecked] = useState(false);
  const [disclaimerIsCheckedWarning, setDisclaimerIsCheckedWarning] =
    useState(false);
  const [termsIsChecked, setTermsIsChecked] = useState(false);
  const [termsIsCheckedWarning, setTermsIsCheckedWarning] = useState(false);
  const [privacyIsChecked, setPrivacyIsChecked] = useState(false);
  const [privacyIsCheckedWarning, setPrivacyIsCheckedWarning] = useState(false);

  const [emailSent, setEmailSent] = useState(false);

  // API Start
  const { response, error, axiosFetch } = useAxios();

  // Validate that email is in form of example@domain.com
  const validateEmail = (email) => {
    const emailRegex = /\S+@\S+\.\S+/;
    return emailRegex.test(email);
  };

  const handleSubmit = () => {
    // Verify that an email has been entered, and that it is in email format.
    if (
      email &&
      validateEmail(email) &&
      disclaimerIsChecked &&
      termsIsChecked &&
      privacyIsChecked
    ) {
      axiosFetch({
        axiosInstance: jsonFormat(),
        method: "POST",
        url: "user/register",
        sendSignal: false,
        requestConfig: {
          email: email,
          authentication_origin: "clinical_dashboard",
          disclaimer_checked: disclaimerIsChecked,
          disclaimer_version: "CD-DS-20230901",
          terms_checked: termsIsChecked,
          terms_version: "SSI-TOS-20230901",
          privacy_checked: privacyIsChecked,
          privacy_version: "SSI-PP-20230901",
        },
      });
    } else {
      toastManager(
        "warning",
        "Account cannot be created.",
        "Please enter a valid email address, read and accept all disclaimers and policies."
      );
    }
    !email || !validateEmail(email)
      ? setEmailWarning(true)
      : setEmailWarning(false);

    !disclaimerIsChecked
      ? setDisclaimerIsCheckedWarning(true)
      : setDisclaimerIsCheckedWarning(false);

    !termsIsChecked
      ? setTermsIsCheckedWarning(true)
      : setTermsIsCheckedWarning(false);

    !privacyIsChecked
      ? setPrivacyIsCheckedWarning(true)
      : setPrivacyIsCheckedWarning(false);
  };

  // RESPONSE Detection
  useEffect(() => {
    // 1: Custom action code handling.
    // Account successfully created.
    if (response) {
      if (response?.action_code === "email_sent") {
        setEmailSent(true);

        toastManager(
          "success",
          "Email approved!",
          "Follow the link in your email to complete registration."
        );
      }
    }
  }, [response]);

  // ERROR Detection
  useEffect(() => {
    if (error) {
      // 1: Custom action_code handling.
      // Email is already being used
      if (error?.email?.action_code === "email_taken") {
        toastManager(
          "warning",
          "Email Taken!",
          "This email is already being used. Use a different email or log-in."
        );
      }
    }
  }, [error]);
  // API End

  // Detect enter press
  useEnterKeyPress(handleSubmit);

  return (
    <AuthenticationCard
      AuthenticationPromptTitle={"Sign Up"}
      AuthenticationPromptText={
        "Don't have a Dermi.ai account? Create one below."
      }
      AuthenticationButtonPrompt={"Register"}
      AuthenticationButtonFunction={handleSubmit}
      redirectOption={"login"}
    >
      {emailSent && (
        <EmailSentPrompt
          title={"Email Sent!"}
          prompt={
            "Follow the link in your email to complete registration. You can close this tab."
          }
        />
      )}

      <RegularField
        fieldLabel="Email"
        placeholderText="Enter your email"
        setFunction={setEmail}
        value={email}
        fieldWarning={emailWarning}
      />

      <PolicyCheckbox
        checked={disclaimerIsChecked}
        setChecked={setDisclaimerIsChecked}
        promptText="I have read the"
        policyName="Clinical Dashboard Disclaimer"
        policyLink="https://dermi.ai/clinical-dashboard-disclaimer"
        fieldWarning={disclaimerIsCheckedWarning}
        setFieldWarning={setDisclaimerIsCheckedWarning}
      />

      <PolicyCheckbox
        checked={termsIsChecked}
        setChecked={setTermsIsChecked}
        promptText="I have read, and I agree to the"
        policyName="Terms of Service"
        policyLink="https://dermi.ai/terms"
        fieldWarning={termsIsCheckedWarning}
        setFieldWarning={setTermsIsCheckedWarning}
      />

      <PolicyCheckbox
        checked={privacyIsChecked}
        setChecked={setPrivacyIsChecked}
        promptText="I have read, and I agree to the"
        policyName="Privacy Policy"
        policyLink="https://dermi.ai/privacy-policy"
        fieldWarning={privacyIsCheckedWarning}
        setFieldWarning={setPrivacyIsCheckedWarning}
      />
    </AuthenticationCard>
  );
};

export default EmailRegistrationScreen;
